import { DateTime } from "luxon"

import { EventDetailsPlaceholder } from "components/event-details-placeholder"
import { EventData } from "types"
import { ReactComponent as PinFilled } from "assets/pin-filled.svg"
import { ReactComponent as CalendarAlt } from "assets/calendar-alt.svg"

const getEventTimezone = (eventData: EventData): string | undefined => {
  return eventData.timezone
}

// Remove all HTML tags from a string, including incomplete multi-tag sanitization.
function stripHtmlTags(str: string): string {
  let prev;
  do {
    prev = str;
    str = str.replace(/<[^>]*>/g, "");
  } while (str !== prev);
  return str;
}

// Parse an ISO string into a DateTime in the event timezone.
// Handles both date-only strings (YYYY-MM-DD) and full ISO with/without offset.
const parseInEventZone = (iso: string, zone?: string): DateTime => {
  if (!zone) {
    return DateTime.fromISO(iso)
  }

  const includesTime = /[Tt]/.test(iso)
  const includesOffset = /([zZ]|[+-]\d{2}:?\d{2})/.test(iso)

  if (!includesTime) {
    // Date-only: interpret as midnight in the event timezone
    return DateTime.fromISO(iso, { zone })
  }

  if (includesOffset) {
    // ISO defines an instant; convert that instant to the event timezone
    return DateTime.fromISO(iso, { setZone: true }).setZone(zone)
  }

  // Time provided but no offset; interpret directly in the event timezone
  return DateTime.fromISO(iso, { zone })
}

const EventDetails = ({ eventData }: { eventData: EventData | undefined }) => {
  if (!eventData) {
    return <EventDetailsPlaceholder />
  }

  const eventZone = getEventTimezone(eventData)
  const start = parseInEventZone(eventData.start_date, eventZone)
  const end = parseInEventZone(eventData.end_date, eventZone)
  const isSingleDay = start.toISODate() === end.toISODate()

  return (
    <div className="container px-8 pt-12 pb-8 mx-auto">
      <div className="flex flex-col gap-6 md:flex-row md:gap-8">
        {eventData.branding_image && (
          <div className="flex-shrink-0 w-full md:w-[250px]">
            <img
              src={eventData.branding_image}
              alt={`${eventData.event_name} branding`}
              className="w-full md:w-[250px] md:h-[125px] object-cover rounded-lg"
              width={600}
              height={300}
            />
          </div>
        )}
        <div className="flex-1">
          <h1
            className="mb-4 text-3xl font-bold text-center md:text-left lg:w-8/12"
            aria-label={`Event Name: ${eventData.event_name}`}
          >
            {eventData.event_name}
          </h1>
          <div className="flex flex-col md:flex-row justify-center md:justify-start items-left md:gap-8 text-theme_text_medium_plus">
            <div className="flex items-center gap-2">
              <CalendarAlt fill="var(--theme_accent)" className="w-10 h-10" aria-hidden="true" title="calendar icon" />
              <div className="flex flex-col" aria-label={`Event Dates: ${start.toLocaleString(DateTime.DATE_HUGE)}${isSingleDay ? "" : ` to ${end.toLocaleString(DateTime.DATE_HUGE)}`}`}>
                {isSingleDay ? (
                  <span className="text-med">
                    {start.toLocaleString(DateTime.DATE_HUGE)}
                  </span>
                ) : (
                  <>
                    <span className="text-med">
                      {start.toLocaleString(DateTime.DATE_HUGE)}
                      <span> -</span>
                    </span>
                    <span className="text-med">
                      {end.toLocaleString(DateTime.DATE_HUGE)}
                    </span>
                  </>
                )}
              </div>
            </div>
            <div className="hidden md:block w-[2px] h-8 bg-theme_text_light"></div>

            <div className="flex gap-2 items-center">
              <PinFilled
                fill="var(--theme_accent)"
                className="w-10 h-10"
                aria-hidden="true"
                title="map pin"
              />
              <div>
                <div
                  className="text-lg"
                  aria-label={`Event Location Name: ${eventData.location.name["en-US"]}`}
                >
                  {eventData.location.name["en-US"]}
                </div>
                <div
                  className="text-sm"
                  aria-label={`Event Location Address: ${eventData.location.address.address}`}
                >
                  {eventData.location.address.address}
                </div>
              </div>
            </div>
          </div>
          <div
            className="text-med pt-6 lg:w-8/12 text-theme_text_medium_plus [&_a]:text-theme_accent [&_a]:underline [&_a:hover]:text-theme_accent_light [&_a:hover]:transition-colors [&_a:hover]:duration-200"
            dangerouslySetInnerHTML={{ __html: eventData.description }}
            aria-label={`Event Description: ${stripHtmlTags(eventData.description)}`}
          />
        </div>
      </div>
    </div>
  )
}

export { EventDetails }
